// MobileConfigureSheet — the design's `.mqr-sheet` request-configure step.
// Header (item hero for one item, or "N resources from M catalogs" for many),
// resources list, Recipient row → multi-select picker, Duration row → preset +
// custom picker, reason, full-width Send, and Clear basket. Reuses the same
// recipient/basket/submit state the desktop modal uses.

import { Box, Flex, Icon, SlideFade } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import {
  TbAlertTriangle,
  TbCheck,
  TbChevronRight,
  TbClock,
  TbTrash,
  TbX,
} from "react-icons/tb";
import AppLogo from "components/ds/AppLogo";
import Avatar from "components/ds/Avatar";
import GroupAvatar from "components/ds/GroupAvatar";
import DurationField, {
  type DurationValue,
} from "components/ds/DurationField";
import PloyButton from "components/ds/PloyButton";
import StatusPill from "components/ds/StatusPill";
import { durationLabel } from "components/ds/durationLabel";
import { pluralise } from "modules/utils";
import type { Basket } from "../useBasket";
import type { RecipientSelection } from "../useRecipientSelection";
import type { IdentitySelection } from "../useIdentitySelection";
import {
  allResourcesAllowIndefinite,
  basketIndefiniteOnly,
  capDurationPresets,
} from "../durationLimits";
import ReasonField from "../ReasonField";
import ReasonScopeSelector from "../ReasonScopeSelector";
import RolePicker from "components/RolePicker";
import { requestableRoles, selectedRoleIds } from "../roleSelection";
import { perItemKey, resolveRoleDuration } from "../payloads";
import { sharedApprovalTone } from "../recipientViews";
import { EVERY_RECIPIENT, type FlatRequestItem } from "../types";
import type { BasketRecipient } from "../BasketDrawer";
import MobileSheet from "./MobileSheet";
import { catalogLinkName } from "state/members/access_v2/catalogNames";
import type { RequestFor } from "state/members/access_v2/roleRequests";

// DurationField only supports up to months, so "1 year" is expressed as 12mo.
const DURATION_PRESETS: { label: string; value: DurationValue }[] = [
  { label: "1 week", value: { num: 1, unit: "weeks" } },
  { label: "1 month", value: { num: 1, unit: "months" } },
  { label: "3 months", value: { num: 3, unit: "months" } },
  { label: "6 months", value: { num: 6, unit: "months" } },
  { label: "1 year", value: { num: 12, unit: "months" } },
];

const sameDuration = (a: DurationValue, b: DurationValue) =>
  a.num === b.num && a.unit === b.unit;

const ROW = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  w: "full",
  py: "14px",
  px: "2xs",
  textAlign: "left" as const,
  borderBottomWidth: "1px",
  borderBottomStyle: "solid" as const,
  borderBottomColor: "neutral.200",
  cursor: "pointer",
  transition: "background 120ms",
  _hover: { bg: "neutral.50" },
};

const MobileConfigureSheet = ({
  isOpen,
  onClose,
  basket,
  selection,
  identity,
  onSubmit,
  isSubmitting,
  canSubmit,
  submitHint,
}: {
  isOpen: boolean;
  onClose: () => void;
  basket: Basket;
  selection: RecipientSelection;
  identity: IdentitySelection;
  onSubmit: () => void;
  isSubmitting: boolean;
  canSubmit: boolean;
  submitHint?: string | null;
}) => {
  const [picker, setPicker] = useState<"recipient" | "duration" | null>(null);
  const items = basket.basketItems;
  const { recipients, pickerPeople, selectedIds } = selection;
  const isMulti = items.length > 1;
  // A role request sends only its roles' durations, each set in its RolePicker,
  // so a basket of nothing else has no duration of its own to set.
  const onlyRoleRequests = items.every((i) => Boolean(requestableRoles(i)));
  const catalogCount = new Set(items.map((i) => i.catalogName)).size;

  // Offer Indefinite only when every selected resource allows it
  // (see allResourcesAllowIndefinite — same rule as the desktop BasketDrawer).
  const resources = basket.basketTerms;
  const allowIndefinite = allResourcesAllowIndefinite(resources);
  const indefiniteOnly = basketIndefiniteOnly(resources);

  // Suggest only durations within the basket's most-restrictive cap (plus the
  // cap itself) — longer values stay reachable via the Custom field, where the
  // row warnings flag them. Same rule as the desktop BasketDrawer.
  const durationPresets = useMemo(
    () => capDurationPresets(DURATION_PRESETS, resources),
    [resources],
  );

  // Mobile has no per-item mode, so the single bulk reason covers every item.
  const reasonRequired = basket.anyReasonRequired;
  const [reasonTouched, setReasonTouched] = useState(false);
  // The info bar signals "required" up front; the red error only shows once the
  // user has left the field empty, not on open.
  const showReasonError =
    reasonRequired && reasonTouched && basket.bulkReason.trim() === "";
  const reasonRequiredCount = basket.reasonRequiredCount;
  const submittableCount = basket.submittableItems.length;
  const reasonScopeCount = basket.reasonOnlyRequired
    ? reasonRequiredCount
    : submittableCount;

  const bulkDurationLabel = basket.bulkDuration.indefinite
    ? "Indefinite"
    : durationPresets.find((p) => sameDuration(p.value, basket.bulkDuration))
        ?.label ?? durationLabel(basket.bulkDuration);

  const hasMe = recipients.some((r) => r.suffix === "(Myself)");
  const soleRecipient =
    recipients.length === 1 && !recipients[0].isGroup
      ? recipients[0]
      : undefined;
  // No per-recipient rows here, so each says whose request on it won't be sent.
  const itemWarnings = (item: FlatRequestItem): string[] =>
    recipients.flatMap((r) => {
      const status = basket.rowStatus[perItemKey(r.id, item.id)];
      const message =
        status?.warning ??
        (r.id === identity.selfRecipientId ? identity.problem(item.id) : null) ??
        status?.note;
      if (!message) return [];
      return [recipients.length === 1 ? message : `${r.name}: ${message}`];
    });
  const itemTone = (item: FlatRequestItem) =>
    sharedApprovalTone(
      recipients.flatMap((r) => {
        const view = basket.recipientItem(r.id, item);
        return view &&
          !basket.rowStatus[perItemKey(r.id, item.id)]?.warning &&
          !(r.id === identity.selfRecipientId && identity.problem(item.id))
          ? [{ view, selection: basket.rolesFor(r.id) }]
          : [];
      }),
    );

  // No per-recipient rows here either, so a role's duration is the same for
  // everyone the request is for.
  const rolePicker = (
    item: FlatRequestItem,
    recipientId: string,
    everyone: boolean,
    label: string,
    holder: BasketRecipient | undefined,
  ) => {
    const requestFor: RequestFor = holder
      ? holder.suffix === "(Myself)"
        ? "you"
        : { name: holder.name }
      : "them";
    const view = basket.recipientItem(recipientId, item);
    const roles = view && requestableRoles(view);
    return view && roles ? (
      <Box key={`${item.id}__${recipientId}`} py="sm">
        <RolePicker
          roles={roles}
          selectedIds={selectedRoleIds(view, basket.rolesFor(recipientId))}
          onToggle={(roleId) =>
            basket.toggleRole(recipientId, item, roleId, everyone)
          }
          showHolding={Boolean(holder) && !holder?.isGroup}
          requestFor={requestFor}
          label={label}
          durations={{
            value: (role) =>
              resolveRoleDuration(
                EVERY_RECIPIENT,
                view,
                role,
                undefined,
                basket,
              ),
            onChange: (roleId, d) =>
              basket.patchRoleDuration(EVERY_RECIPIENT, item, roleId, d),
          }}
        />
      </Box>
    ) : null;
  };
  const recipientLabel =
    recipients.length === 0
      ? "Select recipient"
      : recipients.length === 1
        ? `${recipients[0].name}${recipients[0].suffix ? ` ${recipients[0].suffix}` : ""}`
        : hasMe
          ? `You + ${recipients.length - 1} other${recipients.length === 2 ? "" : "s"}`
          : `${recipients.length} people`;

  if (items.length === 0) return null;
  const singleTone = isMulti ? null : itemTone(items[0]);

  return (
    <MobileSheet isOpen={isOpen} onClose={onClose} label="Request access">
      {/* Header */}
      <Flex align={isMulti ? "center" : "flex-start"} gap="14px" pb="sm">
        {!isMulti && (
          <AppLogo
            size="xl"
            name={items[0].catalogItem.name}
            logoUrl={items[0].catalogItem.imageUrl || undefined}
          />
        )}
        <Box flex="1" minW={0} display="flex" flexDirection="column" gap="2px">
          <Box
            fontFamily="heading"
            fontSize="xl" // 20px
            fontWeight={500}
            letterSpacing="-0.01em"
            lineHeight="1.2"
            color="neutral.950"
            sx={{ textWrap: "balance" }}
          >
            {isMulti ? "Request access" : catalogLinkName(items[0].resource)}
          </Box>
          {isMulti ? (
            <Box fontSize="smd" color="neutral.600">
              {items.length} resources from {catalogCount} catalog
              {catalogCount === 1 ? "" : "s"}
            </Box>
          ) : (
            <>
              <Box fontSize="md" color="neutral.800" lineHeight="1.3">
                {items[0].catalogItem.name}
              </Box>
              <Box fontSize="smd" color="neutral.600">
                From{" "}
                <Box as="strong" color="neutral.900" fontWeight={600}>
                  {items[0].catalogName.replace(/ catalog$/i, "")}
                </Box>
              </Box>
              {singleTone && (
                <Box mt="2xs">
                  <StatusPill tone={singleTone} />
                </Box>
              )}
            </>
          )}
        </Box>
        <Box
          as="button"
          type="button"
          aria-label="Close"
          onClick={onClose}
          w="32px"
          h="32px"
          borderRadius="component.lg"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          color="neutral.700"
          flexShrink={0}
          transition="background 120ms"
          _hover={{ bg: "neutral.100" }}
        >
          <Icon as={TbX} boxSize="18px" />
        </Box>
      </Flex>

      {/* Single resource: surface the over-policy duration warning the
          multi-item list shows per row. Without this a single-resource request
          can get its submit silently disabled (the row is dropped from
          submittableItems) with no explanation. */}
      {!isMulti && itemWarnings(items[0]).length > 0 && (
        <Flex
          align="center"
          gap="6px"
          p="8px 10px"
          mb="sm"
          bg="warning.50"
          borderWidth="1px"
          borderStyle="solid"
          borderColor="warning.200"
          borderRadius="component.xl"
        >
          <Icon as={TbAlertTriangle} boxSize="13px" color="warning.600" flexShrink={0} />
          <Box fontSize="xs" color="warning.800">
            {itemWarnings(items[0]).map((w) => (
              <Box key={w}>{w}</Box>
            ))}
          </Box>
        </Flex>
      )}

      {/* Multi: resources list */}
      {isMulti && (
        <Box
          display="flex"
          flexDirection="column"
          gap="sm"
          pb="2xs"
          borderBottomWidth="1px"
          borderBottomStyle="solid"
          borderBottomColor="neutral.200"
        >
          <Flex align="center" justify="space-between">
            <Box fontSize="15px" fontWeight={500} color="azurite.600">
              Resources
            </Box>
            <Box
              minW="22px"
              h="20px"
              px="7px"
              bg="azurite.100"
              color="azurite.700"
              fontSize="11px"
              fontWeight={600}
              borderRadius="full"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
            >
              {items.length}
            </Box>
          </Flex>
          <Box
            display="flex"
            flexDirection="column"
            gap="1xs"
            maxH="240px"
            overflowY="auto"
          >
            {items.map((it) => {
              // A row with a warning won't be sent (its duration is over the
              // resource's policy max). Desktop shows this as a chip; mobile
              // surfaces the reason inline since there's no hover for a tooltip.
              const warnings = itemWarnings(it);
              const warning = warnings.length > 0;
              const tone = itemTone(it);
              return (
                <Flex
                  key={it.id}
                  align="center"
                  gap="10px"
                  p="8px 10px"
                  bg={warning ? "warning.50" : "neutral.25"}
                  borderWidth="1px"
                  borderStyle="solid"
                  borderColor={warning ? "warning.200" : "neutral.200"}
                  borderRadius="component.xl" // 10px
                >
                  <AppLogo
                    size="sm"
                    name={it.catalogItem.name}
                    logoUrl={it.catalogItem.imageUrl || undefined}
                  />
                  <Box flex="1" minW={0}>
                    <Box
                      fontSize="md"
                      fontWeight={600}
                      color="neutral.950"
                      noOfLines={1}
                    >
                      {catalogLinkName(it.resource)}
                    </Box>
                    <Box fontSize="xs" color="neutral.700" noOfLines={1}>
                      {it.catalogItem.name} ·{" "}
                      {it.catalogName.replace(/ catalog$/i, "")}
                    </Box>
                    {warnings.map((w) => (
                      <Flex key={w} align="center" gap="4px" mt="2px">
                        <Icon
                          as={TbAlertTriangle}
                          boxSize="11px"
                          color="warning.600"
                          flexShrink={0}
                        />
                        <Box fontSize="xs" color="warning.800" noOfLines={2}>
                          {w}
                        </Box>
                      </Flex>
                    ))}
                    {tone && (
                      <Box mt="2px">
                        <StatusPill tone={tone} />
                      </Box>
                    )}
                  </Box>
                  <Box
                    as="button"
                    type="button"
                    aria-label={`Remove ${catalogLinkName(it.resource)}`}
                    onClick={() => basket.toggle(it.id)}
                    w="28px"
                    h="28px"
                    borderRadius="component.md"
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    color="neutral.600"
                    flexShrink={0}
                    transition="background 120ms, color 120ms"
                    _hover={{ bg: "neutral.100", color: "error.600" }}
                  >
                    <Icon as={TbX} boxSize="14px" />
                  </Box>
                </Flex>
              );
            })}
          </Box>
        </Box>
      )}

      {items.map((it) => {
        const roleLabel = isMulti
          ? `Roles of ${catalogLinkName(it.resource)}`
          : "Roles";
        if (basket.sharesRoles(it)) {
          const shownFor =
            recipients.find((r) => basket.recipientItem(r.id, it))?.id ??
            EVERY_RECIPIENT;
          return rolePicker(it, shownFor, true, roleLabel, soleRecipient);
        }
        return recipients.map((r) =>
          rolePicker(
            it,
            r.id,
            false,
            `${roleLabel} for ${r.name}`,
            r,
          ),
        );
      })}

      {/* Recipient row */}
      <Box
        as="button"
        type="button"
        onClick={() => setPicker(picker === "recipient" ? null : "recipient")}
        {...ROW}
      >
        <Box fontSize="15px" fontWeight={500} color="azurite.600" flexShrink={0}>
          Recipient
          {recipients.length > 1 && (
            <Box as="span" color="neutral.600" fontWeight={400}>
              {" "}
              · {recipients.length}
            </Box>
          )}
        </Box>
        <Flex
          align="center"
          gap="sm"
          flex="1 1 0"
          minW={0}
          justify="flex-end"
          fontSize="md"
          color="neutral.950"
        >
          <Flex align="center" sx={{ "& > *": { ml: "-6px" }, "& > *:first-of-type": { ml: 0 } }}>
            {recipients.slice(0, 3).map((r) =>
              r.isGroup ? (
                <GroupAvatar key={r.id} count={r.groupCount ?? 0} size="sm" />
              ) : (
                <Avatar
                  key={r.id}
                  name={r.name}
                  src={r.avatarSrc}
                  tone={r.tone}
                  size="sm"
                />
              ),
            )}
          </Flex>
          <Box noOfLines={1}>{recipientLabel}</Box>
          <Icon as={TbChevronRight} boxSize="18px" color="neutral.500" flexShrink={0} />
        </Flex>
      </Box>
      {picker === "recipient" && (
        <SlideFade in offsetY="-6px" style={{ marginTop: "-6px" }}>
        <Box
          display="flex"
          flexDirection="column"
          gap="2px"
          p="8px"
          bg="neutral.25"
          borderWidth="1px"
          borderStyle="solid"
          borderColor="neutral.200"
          borderRadius="component.2xl"
        >
          <Box px="12px" pt="8px" pb="4px" fontSize="xs" color="neutral.600" lineHeight="1.4">
            Select one or more. Request is sent to each person's manager.
          </Box>
          {pickerPeople.map((p) => {
            const on = selectedIds.includes(p.id);
            return (
              <Flex
                as="button"
                type="button"
                key={p.id}
                onClick={() => selection.toggle(p.id)}
                align="center"
                gap="10px"
                p="10px 12px"
                borderRadius="component.lg"
                cursor="pointer"
                textAlign="left"
                bg={on ? "azurite.50" : "transparent"}
                color={on ? "azurite.800" : "neutral.950"}
                fontWeight={on ? 500 : 400}
                transition="background 100ms"
                _hover={{ bg: on ? "azurite.50" : "neutral.100" }}
              >
                <Flex
                  align="center"
                  justify="center"
                  w="18px"
                  h="18px"
                  flexShrink={0}
                  borderRadius="5px"
                  borderWidth="1.5px"
                  borderStyle="solid"
                  borderColor={on ? "azurite.500" : "neutral.400"}
                  bg={on ? "azurite.500" : "neutral.25"}
                  color="white"
                >
                  {on && <TbCheck size={13} strokeWidth={3} />}
                </Flex>
                {p.isGroup ? (
                  <GroupAvatar count={p.groupCount ?? 0} size="sm" />
                ) : (
                  <Avatar name={p.name} src={p.avatarSrc} size="sm" />
                )}
                <Box flex="1" minW={0} fontSize="md" noOfLines={1}>
                  {p.name}
                  {p.suffix ? ` ${p.suffix}` : ""}
                </Box>
              </Flex>
            );
          })}
          <Flex justify="flex-end" pt="8px" mt="6px" borderTopWidth="1px" borderTopColor="neutral.100">
            <PloyButton variant="primary" size="sm" onClick={() => setPicker(null)}>
              Done
            </PloyButton>
          </Flex>
        </Box>
        </SlideFade>
      )}

      {/* Duration row */}
      {!onlyRoleRequests && (
        <Box
          as="button"
          type="button"
          onClick={() => setPicker(picker === "duration" ? null : "duration")}
          {...ROW}
        >
          <Box fontSize="15px" fontWeight={500} color="azurite.600" flexShrink={0}>
            Duration
            {isMulti && (
              <Box as="span" color="neutral.600" fontWeight={400}>
                {" "}
                · all items
              </Box>
            )}
          </Box>
          <Flex align="center" gap="sm" justify="flex-end" fontSize="md" color="neutral.950">
            <Box as="strong" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>
              {bulkDurationLabel}
            </Box>
            <Icon as={TbChevronRight} boxSize="18px" color="neutral.500" flexShrink={0} />
          </Flex>
        </Box>
      )}
      {!onlyRoleRequests && picker === "duration" && (
        <SlideFade in offsetY="-6px" style={{ marginTop: "-6px" }}>
        <Box
          display="flex"
          flexDirection="column"
          gap="2px"
          p="8px"
          bg="neutral.25"
          borderWidth="1px"
          borderStyle="solid"
          borderColor="neutral.200"
          borderRadius="component.2xl"
        >
          {durationPresets.map((preset) => {
            const on =
              !basket.bulkDuration.indefinite &&
              sameDuration(preset.value, basket.bulkDuration);
            return (
              <Flex
                as="button"
                type="button"
                key={preset.label}
                onClick={() => {
                  basket.setBulkDuration(preset.value);
                  setPicker(null);
                }}
                align="center"
                gap="10px"
                p="10px 12px"
                borderRadius="component.lg"
                cursor="pointer"
                textAlign="left"
                bg={on ? "azurite.50" : "transparent"}
                color={on ? "azurite.800" : "neutral.950"}
                fontWeight={on ? 500 : 400}
                _hover={{ bg: on ? "azurite.50" : "neutral.100" }}
              >
                <Icon as={TbClock} boxSize="16px" color="neutral.600" />
                <Box flex="1" fontSize="md">
                  {preset.label}
                </Box>
                {on && <Icon as={TbCheck} boxSize="16px" color="azurite.600" />}
              </Flex>
            );
          })}
          {allowIndefinite && (
            <Flex
              as="button"
              type="button"
              onClick={() => {
                basket.setBulkDuration({
                  ...basket.bulkDuration,
                  indefinite: true,
                });
                setPicker(null);
              }}
              align="center"
              gap="10px"
              p="10px 12px"
              borderRadius="component.lg"
              cursor="pointer"
              textAlign="left"
              bg={basket.bulkDuration.indefinite ? "azurite.50" : "transparent"}
              color={basket.bulkDuration.indefinite ? "azurite.800" : "neutral.950"}
              fontWeight={basket.bulkDuration.indefinite ? 500 : 400}
              _hover={{ bg: basket.bulkDuration.indefinite ? "azurite.50" : "neutral.100" }}
            >
              <Icon as={TbClock} boxSize="16px" color="neutral.600" />
              <Box flex="1" fontSize="md">
                Indefinite
              </Box>
              {basket.bulkDuration.indefinite && (
                <Icon as={TbCheck} boxSize="16px" color="azurite.600" />
              )}
            </Flex>
          )}
          {!indefiniteOnly && (
            <Flex align="center" gap="sm" p="8px 12px">
              <Box fontSize="md" color="neutral.700" flexShrink={0}>
                Custom
              </Box>
              <Box flex="1" display="flex" justifyContent="flex-end">
                <DurationField
                  size="sm"
                  value={basket.bulkDuration}
                  allowIndefinite={allowIndefinite}
                  onChange={basket.setBulkDuration}
                />
              </Box>
            </Flex>
          )}
        </Box>
        </SlideFade>
      )}

      {/* Reason */}
      <Box mt="2xs">
        <ReasonField
          required={reasonRequired}
          error={showReasonError}
          value={basket.bulkReason}
          onChange={basket.setBulkReason}
          onBlur={() => setReasonTouched(true)}
          rows={2}
          barText={`${reasonRequiredCount} ${pluralise(
            "resource",
            reasonRequiredCount,
          )} in this request require a reason for your manager.`}
          placeholder={
            reasonRequired
              ? `Provide bulk reasoning to ${reasonScopeCount} ${pluralise(
                  "resource",
                  reasonScopeCount,
                )}`
              : "Add a short reason for your manager (optional)…"
          }
        />
        {reasonRequired && reasonRequiredCount < submittableCount && (
          <ReasonScopeSelector
            onlyRequired={basket.reasonOnlyRequired}
            onChange={basket.setReasonOnlyRequired}
            requiredCount={reasonRequiredCount}
            totalCount={submittableCount}
          />
        )}
      </Box>

      {/* Submit */}
      {submitHint && (
        <Flex direction="column" align="center" gap="1xs">
          <Box
            fontSize="xs"
            color={basket.recipientOptions.error ? "error.700" : "neutral.600"}
            textAlign="center"
            role={basket.recipientOptions.error ? "alert" : "status"}
          >
            {submitHint}
          </Box>
          {basket.recipientOptions.retry && (
            <PloyButton
              variant="ghost"
              size="sm"
              onClick={basket.recipientOptions.retry}
            >
              Try again
            </PloyButton>
          )}
        </Flex>
      )}
      <PloyButton
        variant="primary"
        onClick={onSubmit}
        isDisabled={!canSubmit}
        isLoading={isSubmitting}
        w="full"
        minH="44px"
        fontSize="15px"
        mt="1xs"
      >
        Request access{isMulti ? ` · ${items.length} items` : ""}
      </PloyButton>

      <Box fontSize="xs" color="neutral.600" lineHeight="1.5" textAlign="center" px="8px">
        {hasMe && recipients.length === 1 ? (
          <>
            Your <strong>manager</strong> will be notified to approve{" "}
            {isMulti ? "these requests" : "this request"}.
          </>
        ) : recipients.length === 1 ? (
          <>
            {recipients[0].name}'s <strong>manager</strong> will be notified to
            approve {isMulti ? "these requests" : "this request"}.
          </>
        ) : (
          <>
            Each recipient's <strong>manager</strong> will be notified to
            approve.
          </>
        )}
      </Box>

      {/* Clear basket */}
      <Flex
        as="button"
        type="button"
        onClick={() => {
          basket.clear();
          onClose();
        }}
        align="center"
        justify="center"
        gap="1xs"
        w="full"
        py="10px"
        mt="2xs"
        borderRadius="component.lg"
        color="neutral.700"
        fontSize="smd"
        fontWeight={500}
        transition="background 120ms, color 120ms"
        _hover={{ bg: "error.50", color: "error.700" }}
      >
        <Icon as={TbTrash} boxSize="15px" />
        Clear basket
      </Flex>
    </MobileSheet>
  );
};

export default MobileConfigureSheet;
